import React, { forwardRef, useMemo } from 'react';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as AddMediaSVG } from '@assets/icons/add-media.svg';
import { ReactComponent as FavoriteSVG } from '@assets/icons/favorite.svg';
import { ReactComponent as EyePrivateSVG } from '@assets/icons/eye-private.svg';
import moment from 'moment';
import './CampaignCard.less';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AssetPreviewDto, CampaignItemDto } from '@api/Api';
import { setFavoriteCampaignAction } from '@redux/actions/campaignAction';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import Loader from '@components/Loader';
import { ActionToast } from '@components/Toasts';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import CampaignDropDownMenu from '@components/EntitiesDropdownMenu/CampaignDropDownMenu';

const CampaignCard = forwardRef<
  HTMLDivElement,
  {
    campaign: CampaignItemDto;
    page: string;
    onClick?: (id: string) => void;
    kind: 'default' | 'simple';
  }
>(function CampaignCard({ campaign, page, onClick, kind = 'default' }, ref) {
  const { tourData } = useOnboardingProcess();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const assetsArr: AssetPreviewDto[] = useMemo(
    () => campaign?.topAssets || [],
    [campaign]
  );
  const assetsCount: number = useMemo(
    () => campaign.totalAssetsCount || 0,
    [campaign]
  );

  return (
    <div
      ref={ref}
      onClick={() => {
        if (campaign.deletedAt) return;
        if (onClick) onClick(campaign.id);
        else navigate(`/campaigns/single/${campaign.id}`);
      }}
      className={classNames('campaign_card_container', {
        'tour-route-for-approval-start tour-exit': campaign.demo,
        'b-outline': tourData?.type === 'route-for-approval'
      })}
    >
      {kind !== 'simple' && (
        <Tooltip
          placement="bottomRight"
          overlayClassName="toggle_favorite_overlay"
          title={
            !campaign.deletedAt
              ? !campaign.isFavorite
                ? 'Add to favorite'
                : 'Remove from favorite'
              : ''
          }
        >
          <div
            className={classNames(
              campaign.deletedAt && 'favorite_icon_deleted',
              'favorite_icon'
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (campaign.deletedAt) return;
              if (!campaign) return;
              dispatch(
                setFavoriteCampaignAction({
                  campaignId: campaign.id || '',
                  isFavorite: !campaign.isFavorite
                })
              );

              if (!campaign?.isFavorite) return;

              toast(
                <ActionToast
                  title="Project has been removed from favorites"
                  onUndo={undefined}
                  description={undefined}
                  closeToast={undefined}
                />,
                {
                  hideProgressBar: true,
                  bodyClassName: 'toast_container--invitation'
                }
              );
            }}
          >
            <FavoriteSVG
              className={classNames(
                campaign.isFavorite ? 'favorite' : 'non_favorite'
              )}
            />
          </div>
        </Tooltip>
      )}
      <div
        className={classNames(
          'assets',
          `assets_${assetsCount > 4 ? 4 : assetsArr.length || 0}`,
          { isCampaignDeleted: campaign.deletedAt }
        )}
      >
        {assetsArr.length === 0 && (
          <div className="empty">
            <AddMediaSVG />
          </div>
        )}
        {assetsArr.map((el, idx) => {
          const hasMore = assetsCount > 4 && idx === 3;
          if (idx < 4) {
            const imageUrl = assetPreviewThumbnails({
              url: el.previewUrl || '',
              status: el.status,
              type: el.type,
              format: 'img'
            });

            return (
              <div key={idx} className={classNames('item')}>
                {hasMore && !campaign.isPrivate && (
                  <div className="more_overlay">+ {assetsCount - 4}</div>
                )}
                {typeof imageUrl === 'string' ? (
                  imageUrl === 'pending' ? (
                    <Loader
                      isSpinning
                      spinSize={40}
                      classNameWrapper="campaign_asset_loader"
                    />
                  ) : (
                    <img draggable={false} src={imageUrl || ''} alt="" />
                  )
                ) : (
                  imageUrl
                )}
              </div>
            );
          }
          return null;
        })}
      </div>
      <div className="campaign_info">
        <div className="title">
          <Tooltip
            placement="topLeft"
            overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
            title={campaign.name}
          >
            <div className="copy">
              {campaign.isPrivate && <EyePrivateSVG className="private" />}
              <span>{campaign.name}</span>
            </div>
          </Tooltip>
          {kind !== 'simple' && (
            <CampaignDropDownMenu
              campaign={campaign}
              placement="bottomLeft"
              overlayClassName="campaign_menu_overlay"
            />
          )}
        </div>
        <div className="edited">
          {campaign.deletedAt
            ? moment(campaign.deletedAt).fromNow()
            : `Edited ${moment(campaign.lastEventTime).fromNow()}`}
        </div>
      </div>
    </div>
  );
});

export default CampaignCard;
